import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeleteActivity } from '@/hooks/use-activities';
import { useDeleteGroup } from '@/hooks/use-groups';
import { useDeleteInsurance } from '@/hooks/use-insurances';
import { useDeleteProject, useProject } from '@/hooks/use-projects';
import { formatCurrency, getLocalizedText } from '@/lib/utils';
import { ArrowLeft, Euro, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading } = useProject(id!);
  const deleteProject = useDeleteProject();
  const deleteGroup = useDeleteGroup();
  const deleteActivity = useDeleteActivity();
  const deleteInsurance = useDeleteInsurance();
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
  const [activityToDelete, setActivityToDelete] = useState<string | null>(null);
  const [insuranceToDelete, setInsuranceToDelete] = useState<string | null>(null);

  const handleDeleteProject = async () => {
    await deleteProject.mutateAsync(id!);
    navigate('/projecten');
  };

  const handleDeleteGroup = async (groupId: string) => {
    await deleteGroup.mutateAsync(groupId);
    setGroupToDelete(null);
  };

  const handleDeleteActivity = async (activityId: string) => {
    await deleteActivity.mutateAsync(activityId);
    setActivityToDelete(null);
  };

  const handleDeleteInsurance = async (insuranceId: string) => {
    await deleteInsurance.mutateAsync(insuranceId);
    setInsuranceToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Project niet gevonden</p>
        <Button asChild variant="link">
          <Link to="/projecten">Terug naar projecten</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/projecten">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{getLocalizedText(project.name)}</h1>
          <p className="text-muted-foreground">Projectdetails en groepen</p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/projecten/${project.id}/bewerken`}>
            <Pencil className="mr-2 h-4 w-4" />
            Bewerken
          </Link>
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              Verwijderen
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Project verwijderen?</AlertDialogTitle>
              <AlertDialogDescription>
                Weet je zeker dat je "{getLocalizedText(project.name)}" wilt verwijderen? Dit project en alle
                bijbehorende groepen worden verwijderd. Deze actie kan niet ongedaan worden gemaakt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuleren</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteProject}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Verwijderen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Badge variant={project.isActive ? 'success' : 'secondary'}>{project.isActive ? 'Actief' : 'Inactief'}</Badge>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Facturatie</CardTitle>
            <CardDescription>Artikelnummers en voorschot</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Artikelnummer (voorschot)</p>
                <p className="font-medium">{project.depositArticleNumber}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bedrag voorschot</p>
                <p className="font-medium">{project.depositAmount ? formatCurrency(project.depositAmount) : '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Artikelnummer (single kamer)</p>
                <p className="font-medium">{project.singleRoomArticleNumber}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Artikelnummer (saldo factuur)</p>
                <p className="font-medium">{project.balanceArticleNumber}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Artikelnummer (activiteiten)</p>
                <p className="font-medium">{project.activityArticleNumber || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Kostendrager</p>
                <p className="font-medium">{project.defaultCostUnit || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Mollie betaalprofiel</p>
                <p className="font-medium">
                  {project.mollieProfile === 'cc' ? 'Mollie - incl. creditcard' : 'Mollie - excl. creditcard'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Groepen</CardTitle>
              <CardDescription>{project.groups?.length ?? 0} groepen in dit project</CardDescription>
            </div>
            <Button size="sm" asChild>
              <Link to={`/projecten/${project.id}/groepen/nieuw`}>
                <Plus className="mr-2 h-4 w-4" />
                Nieuwe Groep
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {project.groups && project.groups.length > 0 ? (
              <div className="space-y-2">
                {project.groups.map(group => (
                  <div key={group.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <Link to={`/groepen/${group.id}`} className="font-medium text-primary hover:underline">
                          {getLocalizedText(group.name)}
                        </Link>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" asChild>
                        <Link to={`/groepen/${group.id}/bewerken`}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      <AlertDialog
                        open={groupToDelete === group.id}
                        onOpenChange={(open: boolean) => !open && setGroupToDelete(null)}
                      >
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setGroupToDelete(group.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Groep verwijderen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Weet je zeker dat je "{getLocalizedText(group.name)}" wilt verwijderen? Deze actie kan
                              niet ongedaan worden gemaakt.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteGroup(group.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Verwijderen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <Badge variant={group.isActive ? 'success' : 'secondary'}>
                        {group.isActive ? 'Actief' : 'Inactief'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-6">Nog geen groepen aangemaakt</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Optionele Activiteiten</CardTitle>
              <CardDescription>{project.activities?.length ?? 0} activiteiten in dit project</CardDescription>
            </div>
            <Button size="sm" asChild>
              <Link to={`/projecten/${project.id}/activiteiten/nieuw`}>
                <Plus className="mr-2 h-4 w-4" />
                Nieuwe Activiteit
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {project.activities && project.activities.length > 0 ? (
              <div className="space-y-2">
                {project.activities.map(activity => (
                  <div key={activity.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Euro className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{activity.internalName}</p>
                        <p className="text-sm text-muted-foreground">
                          Code: {activity.code} • {formatCurrency(activity.price)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" asChild>
                        <Link to={`/activiteiten/${activity.id}/bewerken`}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      <AlertDialog
                        open={activityToDelete === activity.id}
                        onOpenChange={(open: boolean) => !open && setActivityToDelete(null)}
                      >
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setActivityToDelete(activity.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Activiteit verwijderen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Weet je zeker dat je "{activity.internalName}" wilt verwijderen? Deze actie kan niet
                              ongedaan worden gemaakt.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteActivity(activity.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Verwijderen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <Badge variant={activity.isActive ? 'success' : 'secondary'}>
                        {activity.isActive ? 'Actief' : 'Inactief'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-6">Nog geen activiteiten aangemaakt</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Verzekeringen</CardTitle>
              <CardDescription>{project.insurances?.length ?? 0} verzekeringen in dit project</CardDescription>
            </div>
            <Button size="sm" asChild>
              <Link to={`/projecten/${project.id}/verzekeringen/nieuw`}>
                <Plus className="mr-2 h-4 w-4" />
                Nieuwe Verzekering
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {project.insurances && project.insurances.length > 0 ? (
              <div className="space-y-2">
                {project.insurances.map(insurance => (
                  <div key={insurance.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Euro className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{insurance.internalName}</p>
                        <p className="text-sm text-muted-foreground">
                          Code: {insurance.code} • {formatCurrency(insurance.price)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" asChild>
                        <Link to={`/verzekeringen/${insurance.id}/bewerken`}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      <AlertDialog
                        open={insuranceToDelete === insurance.id}
                        onOpenChange={(open: boolean) => !open && setInsuranceToDelete(null)}
                      >
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setInsuranceToDelete(insurance.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Verzekering verwijderen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Weet je zeker dat je "{insurance.internalName}" wilt verwijderen? Deze actie kan niet
                              ongedaan worden gemaakt.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuleren</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteInsurance(insurance.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Verwijderen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <Badge variant={insurance.isActive ? 'success' : 'secondary'}>
                        {insurance.isActive ? 'Actief' : 'Inactief'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-6">Nog geen verzekeringen aangemaakt</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
